import { ReactElement, useCallback, useRef } from 'react'
import { hasModulePermission } from 'helpers/user'
import { AnyObject } from 'typings/custom'
import { MenuListItemsProps, SubMenuProps } from 'typings/menu'
import { useEscapeKeyDownListener } from './helpers'
import {
  ActiveMobileBar,
  MenuItem,
  MenuLink,
  MenuTextButton,
  MobileMenuItem,
  MobileMenuItemLi,
  MobileMenuItemsUl,
  MobileMenuListItem,
  MobileMenuText,
  MobileSubMenuItem,
  SubMenuItem,
  SubMenuItemContainer,
  SubMenuText
} from './subcomponents'

const statementIds = ['viewStatement', 'emailStatement', 'statements']

const renderSubMenuItems = (
  subMenuItems: Array<SubMenuProps>,
  parentID: string,
  changeActiveMenu: (id: string, sourcePath: string) => void
): Array<ReactElement> => {
  return subMenuItems.map((item: SubMenuProps) => {
    return (
      hasModulePermission(item.menuId) && (
        <li key={item.menuId}>
          <MenuLink
            onClick={(): void => {
              changeActiveMenu(parentID, item.menuId)
            }}
            to={statementIds.includes(item.menuId) ? '#' : item.path}
          >
            <SubMenuText>{item.menuName}</SubMenuText>
          </MenuLink>
        </li>
      )
    )
  })
}

const MenuListItem = ({
  item,
  isMenuActive,
  isActive,
  expandedMenu,
  setExpandedMenu,
  changeActiveMenu
}: MenuListItemsProps | AnyObject): ReactElement => {
  const menuItemFocusableRef = useRef(null)

  const { isSubMenu, subMenuItems = [], menuId, menuName, path } = item

  const isMenuExpanded = expandedMenu === menuName

  const handleOnEscape = useCallback(() => {
    setExpandedMenu('')
  }, [setExpandedMenu])

  useEscapeKeyDownListener({
    handleOnEscape,
    ref: menuItemFocusableRef,
    isMenuExpanded
  })

  return (
    (!isMenuActive &&
      (isSubMenu ? (
        <SubMenuItem
          aria-labelledby={menuId}
          shouldExpand={isMenuExpanded}
          isActive={isActive}
          key={menuId}
          onMouseEnter={(): void => setExpandedMenu(menuName)}
          onMouseLeave={(): void => setExpandedMenu('')}
        >
          <MenuTextButton
            onClick={(): void =>
              setExpandedMenu(isMenuExpanded ? '' : menuName)
            }
            ref={menuItemFocusableRef}
            onFocus={(): void => setExpandedMenu('')}
            aria-expanded={expandedMenu === menuName}
            aria-current={isActive}
            isActive={isActive}
            id={menuId}
          >
            {menuName}
          </MenuTextButton>
          <SubMenuItemContainer>
            {renderSubMenuItems(subMenuItems, menuId, changeActiveMenu)}
          </SubMenuItemContainer>
        </SubMenuItem>
      ) : (
        <MenuItem
          role="navigation"
          aria-labelledby={menuName}
          key={item.menuId}
        >
          <MenuLink
            onFocus={(): void => setExpandedMenu('')}
            isActive={isActive}
            aria-current={isActive}
            onClick={(): void => {
              changeActiveMenu(menuId)
            }}
            to={path}
          >
            {menuName}
          </MenuLink>
        </MenuItem>
      ))) || (
      <MobileMenuListItem
        role="navigation"
        aria-labelledby={menuId}
        key={item.menuId}
      >
        <MobileMenuItem>
          {isActive ? <ActiveMobileBar /> : ''}{' '}
          {menuId === 'summary' ? (
            <MobileSubMenuItem
              isParentLink
              onClick={(): void => {
                changeActiveMenu(menuId)
              }}
              to={path}
              key={menuId}
            >
              {menuName}
            </MobileSubMenuItem>
          ) : (
            <MobileMenuText id={menuId}>{menuName}</MobileMenuText>
          )}
        </MobileMenuItem>
        <MobileMenuItemsUl>
          {subMenuItems.map(
            (subItem: SubMenuProps): ReactElement =>
              hasModulePermission(subItem.menuId) && (
                <MobileMenuItemLi key={subItem.menuId}>
                  <MobileSubMenuItem
                    onClick={(): void => {
                      changeActiveMenu(menuId, subItem.menuId)
                    }}
                    to={
                      statementIds.includes(subItem.menuId) ? '#' : subItem.path
                    }
                  >
                    {subItem.menuName}
                  </MobileSubMenuItem>
                </MobileMenuItemLi>
              )
          )}
        </MobileMenuItemsUl>
      </MobileMenuListItem>
    )
  )
}

export default MenuListItem
